import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import QuaterlyReportModal from '../QuaterlyReportModal';
import SvgCalendar from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/SvgCalendar';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { useStores } from '../../../stores';
import { formatDate } from '../../../core-utils/helper-utils/helper';
import { getStartDateAndEndDate } from '../../../core-utils/custom-hooks/PlanDates';

interface DateFilterProps {
  disableIncurredDate?: boolean;
  disablePaidDate?: boolean;
  sticky?: boolean;
  showNoDateFilterOption?: boolean;
}

const DateFilter: React.FC<DateFilterProps> = observer(
  ({
    disableIncurredDate = false,
    disablePaidDate = false,
    sticky = false,
    showNoDateFilterOption = false,
  }) => {
    const [openDateFilter, setOpenDateFilter] = useState<boolean>(false);

    const { classes } = makeStyles()({
      dateFilterContainer: {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: '27px',
        background: openDateFilter
          ? patientAccessTheme.palette.grey[50]
          : patientAccessTheme.palette.status.white,
        border: openDateFilter
          ? `1px solid ${patientAccessTheme.palette.primary[200]}`
          : `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderRadius: '4px',
        color: openDateFilter
          ? patientAccessTheme.palette.primary.main
          : patientAccessTheme.palette.text.highemphasis,
        padding: '0 0.25rem',
        cursor: sticky ? '' : 'pointer',
      },
    })();

    const quarterlyReportStore = useStores().quaterlyReportStore;
    const {
      incurredStartDate,
      incurredEndDate,
      paidStartDate,
      paidEndDate,
      isIncurredDate,
      isPaidDate,
      minIncurred,
      maxIncurred,
      minPaid,
      maxPaid,
      noDateFilter,
    } = quarterlyReportStore;

    const { startDate, endDate } = getStartDateAndEndDate();

    // Helper to pick correct display dates based on `noDateFilter`
    const displayIncurredStart = noDateFilter
      ? minIncurred
      : incurredStartDate || startDate;
    const displayIncurredEnd = noDateFilter
      ? maxIncurred
      : incurredEndDate || endDate;
    const displayPaidStart = noDateFilter ? minPaid : paidStartDate;
    const displayPaidEnd = noDateFilter ? maxPaid : paidEndDate;

    return (
      <Grid>
        <Grid
          component="button"
          className={classes.dateFilterContainer}
          onClick={() => (sticky ? {} : setOpenDateFilter(true))}
        >
          <SvgCalendar
            height="16px"
            width="16px"
            color={
              openDateFilter
                ? patientAccessTheme.palette.primary.main
                : '#7C98B6'
            }
            style={{
              marginRight: '8px',
            }}
          />

          {!disableIncurredDate &&
          isIncurredDate &&
          !disablePaidDate &&
          isPaidDate ? (
            <>
              <Typography
                fontSize="12px"
                lineHeight="20px"
                fontType="medium"
                margin="0px 4px 0px 0px"
                styleProps={{
                  borderRight: `solid 1px ${patientAccessTheme.palette.grey[300]}`,
                  paddingRight: '7px',
                }}
              >
                Incurred Date:{' '}
                <b>
                  {formatDate(displayIncurredStart, 'MMM YYYY')} -{' '}
                  {formatDate(displayIncurredEnd, 'MMM YYYY')}
                </b>
              </Typography>
              <Typography
                fontSize="12px"
                lineHeight="20px"
                fontType="medium"
                margin="0px 4px 0px 4px"
              >
                Paid Date:{' '}
                <b>
                  {formatDate(displayPaidStart, 'MMM YYYY')} -{' '}
                  {formatDate(displayPaidEnd, 'MMM YYYY')}
                </b>
              </Typography>
            </>
          ) : (
            <>
              {!disableIncurredDate && isIncurredDate && (
                <Typography
                  fontSize="12px"
                  lineHeight="20px"
                  fontType="medium"
                  margin="0px 4px 0px 0px"
                >
                  Incurred Date:{' '}
                  <b>
                    {formatDate(displayIncurredStart, 'MMM YYYY')} -{' '}
                    {formatDate(displayIncurredEnd, 'MMM YYYY')}
                  </b>
                </Typography>
              )}
              {!disablePaidDate && isPaidDate && (
                <Typography
                  fontSize="12px"
                  lineHeight="20px"
                  fontType="medium"
                  margin="0px 4px 0px 0px"
                >
                  Paid Date:{' '}
                  <b>
                    {formatDate(displayPaidStart, 'MMM YYYY')} -{' '}
                    {formatDate(displayPaidEnd, 'MMM YYYY')}
                  </b>
                </Typography>
              )}
              {disablePaidDate && !isIncurredDate && (
                <Typography
                  fontSize="12px"
                  lineHeight="20px"
                  fontType="medium"
                  margin="0px 4px 0px 0px"
                >
                  Date Range:{' '}
                  <b>
                    {formatDate(
                      displayPaidStart || displayIncurredStart,
                      'MMM YYYY',
                    )}{' '}
                    -{' '}
                    {formatDate(
                      displayPaidEnd || displayIncurredEnd,
                      'MMM YYYY',
                    )}
                  </b>
                </Typography>
              )}
            </>
          )}
        </Grid>

        <QuaterlyReportModal
          open={openDateFilter}
          handleClose={() => {
            setOpenDateFilter(false);
          }}
          saveBtnText="Submit Dates"
          isQuaterlyReport={false}
          setOpen={setOpenDateFilter}
          disablePaidDate={disablePaidDate}
          disableIncurredDate={disableIncurredDate}
          showNoDateFilterOption={showNoDateFilterOption}
        />
      </Grid>
    );
  },
);

export default DateFilter;
